/**
 * Audio File Utilities
 * Functions for extracting metadata from audio files before browser resampling
 */

/**
 * Extract original sample rate from audio file data
 * Supports WAV, MP3, OGG, FLAC, and other common formats
 * @param {ArrayBuffer} arrayBuffer - Raw audio file data
 * @returns {number|null} Original sample rate in Hz, or null if couldn't be determined
 */
export function extractOriginalSampleRate(arrayBuffer) {
  const view = new DataView(arrayBuffer);

  // WAV format detection
  if (arrayBuffer.byteLength >= 44) {
    const riffHeader = String.fromCharCode(
      view.getUint8(0),
      view.getUint8(1),
      view.getUint8(2),
      view.getUint8(3)
    );

    if (riffHeader === 'RIFF') {
      const waveHeader = String.fromCharCode(
        view.getUint8(8),
        view.getUint8(9),
        view.getUint8(10),
        view.getUint8(11)
      );

      if (waveHeader === 'WAVE') {
        // Sample rate is at byte offset 24 (little-endian)
        const sampleRate = view.getUint32(24, true);
        return sampleRate;
      }
    }
  }

  // MP3 format detection (ID3v2 or direct MP3 frame)
  if (arrayBuffer.byteLength >= 4) {
    // Check for ID3v2 tag
    const id3Header = String.fromCharCode(
      view.getUint8(0),
      view.getUint8(1),
      view.getUint8(2)
    );

    let offset = 0;
    if (id3Header === 'ID3') {
      // Skip ID3v2 tag
      const tagSize = ((view.getUint8(6) & 0x7F) << 21) |
                      ((view.getUint8(7) & 0x7F) << 14) |
                      ((view.getUint8(8) & 0x7F) << 7) |
                      (view.getUint8(9) & 0x7F);
      offset = 10 + tagSize;
    }

    // Look for MP3 frame sync
    if (offset + 4 <= arrayBuffer.byteLength) {
      const byte1 = view.getUint8(offset);
      const byte2 = view.getUint8(offset + 1);

      // Check for frame sync (11 bits set)
      if (byte1 === 0xFF && (byte2 & 0xE0) === 0xE0) {
        // Extract MPEG version and layer
        const version = (byte2 >> 3) & 0x03;
        const layer = (byte2 >> 1) & 0x03;
        const byte3 = view.getUint8(offset + 2);
        const samplingRateIndex = (byte3 >> 2) & 0x03;

        // Sample rate table
        const sampleRates = [
          [11025, 12000, 8000],   // MPEG 2.5
          null,                    // Reserved
          [22050, 24000, 16000],   // MPEG 2
          [44100, 48000, 32000]    // MPEG 1
        ];

        if (sampleRates[version] && samplingRateIndex < 3) {
          return sampleRates[version][samplingRateIndex];
        }
      }
    }
  }

  // OGG format detection
  if (arrayBuffer.byteLength >= 27) {
    const oggHeader = String.fromCharCode(
      view.getUint8(0),
      view.getUint8(1),
      view.getUint8(2),
      view.getUint8(3)
    );

    if (oggHeader === 'OggS') {
      // For Vorbis, we need to find the identification header
      // This is a simplified check - real implementation would parse pages
      // Try to find vorbis header at common location (after OGG page header)
      if (arrayBuffer.byteLength >= 60) {
        const vorbisCheck = String.fromCharCode(
          view.getUint8(29),
          view.getUint8(30),
          view.getUint8(31),
          view.getUint8(32),
          view.getUint8(33),
          view.getUint8(34)
        );

        if (vorbisCheck === 'vorbis') {
          // Sample rate is 4 bytes at offset 40 (little-endian)
          const sampleRate = view.getUint32(40, true);
          if (sampleRate > 0 && sampleRate < 200000) {
            return sampleRate;
          }
        }

        // Check for Opus
        const opusCheck = String.fromCharCode(
          view.getUint8(28),
          view.getUint8(29),
          view.getUint8(30),
          view.getUint8(31),
          view.getUint8(32),
          view.getUint8(33),
          view.getUint8(34),
          view.getUint8(35)
        );

        if (opusCheck === 'OpusHead') {
          // Opus input sample rate is at offset 40 (little-endian)
          const sampleRate = view.getUint32(40, true);
          if (sampleRate > 0 && sampleRate < 200000) {
            return sampleRate;
          }
        }
      }
    }
  }

  // FLAC format detection
  if (arrayBuffer.byteLength >= 38) {
    const flacHeader = String.fromCharCode(
      view.getUint8(0),
      view.getUint8(1),
      view.getUint8(2),
      view.getUint8(3)
    );

    if (flacHeader === 'fLaC') {
      // Skip to STREAMINFO block (first metadata block after 'fLaC')
      // Sample rate is in the STREAMINFO block at byte 18-20 (20 bits)
      const byte18 = view.getUint8(18);
      const byte19 = view.getUint8(19);
      const byte20 = view.getUint8(20);

      // Extract 20-bit sample rate
      const sampleRate = ((byte18 << 12) | (byte19 << 4) | (byte20 >> 4));
      if (sampleRate > 0 && sampleRate < 200000) {
        return sampleRate;
      }
    }
  }

  // M4A/AAC/MP4 format detection
  if (arrayBuffer.byteLength >= 8) {
    // Check for ftyp box (MP4/M4A container)
    const boxSize = view.getUint32(0, false);
    const boxType = String.fromCharCode(
      view.getUint8(4),
      view.getUint8(5),
      view.getUint8(6),
      view.getUint8(7)
    );

    if (boxType === 'ftyp') {
      // This is an MP4 container - would need full parser to extract sample rate
      // For AAC, common rates are 44100 or 48000
      // We'll return null and let the audioBuffer.sampleRate be used as fallback
      return null;
    }
  }

  // Couldn't determine format or sample rate
  return null;
}
